import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Button,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useDispatch } from "react-redux";
import {
  deleteImage,
  setProfilePic,
  uploadCroppedProfilePic,
  uploadImagesToS3,
} from "../../redux/authSlice";
import FileUploader from "../commons/FileUploader";
import LabelSelection from "./LabelSelection";
import ProfileImage from "./ProfileImage";
import ProfileImageCropDialog from "./ProfileImageCropDialog";

export default function ProfileImages({
  profile,
  t,
  selectedLabels,
  setSelectedLabels,
  updateLabels,
}) {
  const dispatch = useDispatch();
  const [imageUploaderOpen, setImageUploaderOpen] = useState(false);
  const [accordionExpanded, setAccordionExpanded] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [cropTargetImage, setCropTargetImage] = useState(null);
  const { images, profilePic } = profile;

  const open = Boolean(anchorEl);
  const handleMenuClick = (event, image) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentImage(image);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentImage(null);
  };

  const onFileSelect = async (files) => {
    const formData = new FormData();
    if (files.length > 0) {
      // files.forEach((file) => {
      //   formData.append("images", file, file.name);
      // });
      // dispatch(uploadImages(formData));
      await dispatch(uploadImagesToS3(files));
      setAccordionExpanded(true);
    }
  };

  const handleDeleteImage = (image) => {
    dispatch(deleteImage(image));
  };

  const handleProfilePicChange = async (image, croppedFile) => {
    if (!croppedFile) {
      await dispatch(setProfilePic(image)).unwrap();
      return;
    }

    const uploadedImage = await dispatch(uploadCroppedProfilePic(croppedFile)).unwrap();
    await dispatch(setProfilePic(uploadedImage)).unwrap();
  };
  return (
    <Box>
      <Avatar
        src={profilePic?.url || null}
        alt={profile?.username || t("defaultProfile")}
        sx={{ width: "150px", height: "150px", m: "auto" }}
      />
      <LabelSelection
        profile={profile}
        t={t}
        selectedLabels={selectedLabels}
        setSelectedLabels={setSelectedLabels}
        updateLabels={updateLabels}
      />
      {imageUploaderOpen && (
        <FileUploader
          onFileSelect={onFileSelect}
          acceptedFileTypes={{ "image/*": [] }}
          instructionText={t("dragAndDropImage")}
        />
      )}
      <Accordion
        expanded={accordionExpanded}
        onChange={(_, isExpanded) => setAccordionExpanded(isExpanded)}
        className="my-1"
        sx={{
          backgroundColor: "transparent",
          backgroundImage: "unset",
          boxShadow: "unset",
          "&:before": {
            backgroundColor: "transparent",
          },
        }}
      >
        <AccordionSummary
          sx={{ padding: 0 }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
          className="flex-row-reverse"
        >
          <Box className="flex w-full justify-between items-center">
            <Typography variant="h5">{t("yourPictures")}</Typography>

            <Button
              variant="outlined"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                setImageUploaderOpen((prev) => !prev);
              }}
            >
              {t("upload")}
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {images?.length > 0 && (
            <Box className="my-2 grid grid-cols-2 gap-2">
              {
                images.map((image, index) => (

                  selectedImage === index && (
                    <Box
                      key={image.key}
                      className="col-span-2"
                      sx={{
                        gridColumn: "1 / span 2",
                        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      <img
                        className="w-full rounded-md"
                        src={image.url}
                        alt={t("profileImage")}
                        style={{
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                        }}
                        onClick={() => setSelectedImage(null)}
                      />
                    </Box>

                  )
                ))
              }
              {/* Other images below, each in a single column */}
              {images.map((image, index) => (
                <Box
                  key={image.key}
                  className="group relative"
                  sx={{
                    gridColumn: selectedImage === index ? "1 / span 2" : "auto",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    zIndex: selectedImage === index ? 2 : 1,
                  }}
                  onClick={() => {
                    setSelectedImage(selectedImage === index ? null : index);
                  }}
                >
                  {selectedImage !== index ? (
                    <>
                      <ProfileImage
                        image={image}
                        t={t}
                      />
                      <IconButton
                        aria-label={t("more")}
                        id={`menu-button-${index}`}
                        aria-controls={open ? `menu-${index}` : undefined}
                        aria-expanded={open ? "true" : undefined}
                        aria-haspopup="true"
                        onClick={(e) => handleMenuClick(e, image)}
                        sx={{ position: "absolute", top: 1, right: 1, zIndex: 10 }}
                        className="group-hover:shadow-sm bg-red-500 group-hover:bg-white group-hover:shadow-gray-600"
                      >
                        <MoreVertIcon className="text-black" />
                      </IconButton>
                    </>
                  ) : null}
                </Box>
              )
              )}
            </Box>
          )}
          {!images?.length && (
            <Typography className="text-center pb-2">
              {t("youHaveNoUploads")}
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
      
      {/* Menu for image actions */}
      <Menu
        id="image-menu"
        MenuListProps={{
          "aria-labelledby": "menu-button",
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (currentImage) {
              handleDeleteImage(currentImage);
            }
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{t("delete")}</Typography>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (currentImage) {
              setCropTargetImage(currentImage);
              setIsCropOpen(true);
            }
            setAnchorEl(null);
          }}
        >
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="inherit">{t("makeProfilePicture")}</Typography>
        </MenuItem>
      </Menu>
      <ProfileImageCropDialog
        open={isCropOpen}
        image={cropTargetImage}
        t={t}
        onClose={() => { setIsCropOpen(false); setCurrentImage(null); }}
        onConfirm={async (croppedFile) => {
          await handleProfilePicChange(cropTargetImage, croppedFile);
          setIsCropOpen(false);
          setCurrentImage(null);
        }}
      />
    </Box>
  );
}



// sx={{
//   width: selectedImage ? "100%" : "100%",
//   gridColumn:  "1 / span 2" ,
//   // gridColumn: selectedImage ? "1 / span 2" : "auto",
//   transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
//   zIndex: selectedImage ? 2 : 1,
//   boxShadow: selectedImage ? "0 4px 24px rgba(0,0,0,0.12)" : "none",
//   opacity: selectedImage ? 1 : 0.5,
//   cursor: "pointer"
// }}